import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useReducer,
  PropsWithChildren
} from 'react'
import { RtcProvider } from './RtcContext'
import PropsContext, {
  RtcPropsInterface,
  UIKitUser,
  mediaStore,
  layout,
  CallbacksInterface
} from './PropsContext'
import { MaxUidProvider } from './MaxUidContext'
import AgoraRTC, {
  createClient,
  IAgoraRTCRemoteUser,
  ILocalAudioTrack,
  ILocalVideoTrack,
  UID
} from 'agora-rtc-react'
import { MinUidProvider } from './MinUidContext'
import TracksContext from './TracksContext'
import reducer, { initState } from './Reducer'
import {
  startScreenshare,
  stopScreenshare
} from './Controls/Local/screenshareFunctions'

const useClient = createClient({ codec: 'vp8', mode: 'live' }) // pass in another client if use h264

/**
 * React component that contains the RTC logic. It manages the user state and provides it the children components by wrapping them with context providers.
 */
const RtcConfigure: React.FC<PropsWithChildren<Partial<RtcPropsInterface>>> = (
  props
) => {
  const uid = useRef<UID>()
  const screenTrack = useRef<ILocalVideoTrack>()
  const isScreensharingRef = useRef<boolean>(false)
  const { localVideoTrack, localAudioTrack } = useContext(TracksContext)
  const { callbacks, rtcProps } = useContext(PropsContext)
  const [ready, setReady] = useState<boolean>(false)
  const [channelJoined, setChannelJoined] = useState<boolean>(false)
  let joinRes: ((arg0: boolean) => void) | null = null // Resolve for canJoin -> to set canJoin to true
  const canJoin = useRef(
    new Promise<boolean | void>((resolve, reject) => {
      joinRes = resolve
      console.log(reject)
    })
  )

  let client = useClient()
  if (rtcProps.customRtcClient) {
    // if customRtcClient prop is set then use custom client
    client.removeAllListeners()
    client = rtcProps.customRtcClient
  }

  const dualStreamEnabledRef = useRef<boolean>(false)

  const mediaStore = useRef<mediaStore>({})

  /**
   * Subscribe to a remote user's track, retrying a couple of times on failure. A failed
   * subscription used to be swallowed by a bare `console.log`, leaving the remote tile on the
   * placeholder icon for the whole call with nothing visible in the console to explain why.
   */
  const subscribeWithRetry = async (
    remoteUser: IAgoraRTCRemoteUser,
    mediaType: 'audio' | 'video',
    attempt = 1
  ): Promise<boolean> => {
    try {
      await client.subscribe(remoteUser, mediaType)
      return true
    } catch (error) {
      console.error(
        `[RTCConfigure] Failed to subscribe to ${mediaType} of remote user ${remoteUser.uid} (attempt ${attempt}/3):`,
        error
      )
      if (attempt >= 3) return false
      await new Promise((resolve) => setTimeout(resolve, 1000 * attempt))
      // Only retry while the user is still in the channel and still publishing this media.
      const current = client.remoteUsers.find((u) => u.uid === remoteUser.uid)
      const stillPublished =
        mediaType === 'video' ? current?.hasVideo : current?.hasAudio
      if (!current || !stillPublished) return false
      return subscribeWithRetry(current, mediaType, attempt + 1)
    }
  }

  let { callActive } = props
  if (callActive === undefined) {
    callActive = true
  }

  type stateType = {
    max: UIKitUser[]
    min: UIKitUser[]
  }
  const [uidState, dispatch] = useReducer<React.Reducer<stateType, any>>(
    reducer,
    initState
  )

  // init rtcEngine
  useEffect(() => {
    async function init() {
      try {
        console.log(client)
        client.on('user-joined', async (...args) => {
          const [remoteUser] = args
          if (
            (remoteUser.uid === props.screenshareUid &&
              isScreensharingRef.current) ||
            (remoteUser.uid === 1 && isScreensharingRef.current)
          ) {
          } else {
            mediaStore.current[remoteUser.uid] = {}
          }
          dispatch({
            type: 'user-joined',
            value: args
          })
        })

        client.on('user-published', async (...args) => {
          // Get current peer IDs
          const [remoteUser, mediaType] = args
          console.log('user-published', remoteUser.uid, mediaType)
          // Newer SDKs also announce data channels here; the kit only renders audio and video.
          if (mediaType !== 'audio' && mediaType !== 'video') return
          if (
            (remoteUser.uid === props.screenshareUid &&
              isScreensharingRef.current) ||
            (remoteUser.uid === 1 && isScreensharingRef.current)
          ) {
            dispatch({
              type: 'user-published',
              value: args
            })
          } else {
            const subscribed = await subscribeWithRetry(remoteUser, mediaType)
            if (!subscribed) return
            // The entry is normally created on `user-joined`, but never assume it: assigning
            // onto a missing entry used to throw inside the swallowed promise chain, which left
            // the remote tile on the placeholder for the rest of the call.
            if (!mediaStore.current[remoteUser.uid]) {
              mediaStore.current[remoteUser.uid] = {}
            }
            if (mediaType === 'audio') {
              mediaStore.current[remoteUser.uid].audioTrack =
                remoteUser.audioTrack
              // eslint-disable-next-line no-unused-expressions
              remoteUser.audioTrack?.play()
            } else {
              mediaStore.current[remoteUser.uid].videoTrack =
                remoteUser.videoTrack
              if (rtcProps.enableDualStream && rtcProps.dualStreamMode) {
                client.setStreamFallbackOption(
                  remoteUser.uid,
                  rtcProps.dualStreamMode
                )
              }
            }
            console.log(
              `[RTCConfigure] Subscribed to ${mediaType} of remote user ${remoteUser.uid}`
            )
            dispatch({
              type: 'user-published',
              value: args
            })
          }
        })

        client.on('user-unpublished', async (...args) => {
          const [remoteUser, mediaType] = args
          console.log('user-unpublished', remoteUser.uid)
          if (mediaType === 'audio') {
            // eslint-disable-next-line no-unused-expressions
            remoteUser.audioTrack?.stop()
          }
          dispatch({
            type: 'user-unpublished',
            value: args
          })
          // }
        })

        client.on('connection-state-change', async (...args) => {
          const [curState, prevState, reason] = args
          console.log('connection', prevState, curState, reason)
          if (curState === 'CONNECTED') {
            setChannelJoined(true)
          } else if (curState === 'DISCONNECTED') {
            try {
              stopScreenshare()
              isScreensharingRef.current = false
            } catch (e) {
              console.log('stopscreenshare', e)
            }
            dispatch({ type: 'leave-channel', value: null })
          } else {
            setChannelJoined(false)
          }
        })

        client.on('user-left', (...args) => {
          dispatch({
            type: 'user-left',
            value: args
          })
        })

        if (rtcProps.tokenUrl) {
          const { tokenUrl, channel, uid } = rtcProps
          client.on('token-privilege-will-expire', async () => {
            console.log('token will expire')
            const res = await fetch(
              tokenUrl +
                '/rtc/' +
                channel +
                '/publisher/uid/' +
                (uid || 0) +
                '/'
            )
            const data = await res.json()
            const token = data.rtcToken
            client.renewToken(token)
          })

          client.on('token-privilege-did-expire', async () => {
            const res = await fetch(
              tokenUrl +
                '/rtc/' +
                channel +
                '/publisher/uid/' +
                (uid || 0) +
                '/'
            )
            const data = await res.json()
            const token = data.rtcToken
            client.renewToken(token)
          })
        }

        if (callbacks) {
          const events: [keyof CallbacksInterface] = Object.keys(
            callbacks
          ) as any
          // !!!! fix type, validate event
          events.map((e) => {
            try {
              client.on(e, (...args: any[]) => {
                // eslint-disable-next-line prefer-spread
                ;(callbacks[e] as Function).apply(null, args)
              })
            } catch (e) {
              console.log(e)
            }
          })
        }
        ;(joinRes as (arg0: boolean) => void)(true)
        setReady(true)
      } catch (e) {
        console.log('!!!', e)
      }
    }

    if (joinRes) {
      init()
      return () => {
        try {
          client.removeAllListeners()
        } catch (e) {
          console.log(e)
        }
      }
    } else return () => {}
  }, [rtcProps.appId]) //, ready])

  // Dynamically switches channel when channel prop changes
  useEffect(() => {
    let ignore = false
    async function join(): Promise<void> {
      await canJoin.current
      const { tokenUrl, channel, uid: userUid, appId, token } = rtcProps
      if (client && !ignore) {
        if (rtcProps.role === 'audience') {
          client.setClientRole(rtcProps.role)
        } else {
          client.setClientRole('host')
        }
        if (tokenUrl) {
          try {
            const res = await fetch(
              tokenUrl +
                '/rtc/' +
                channel +
                '/publisher/uid/' +
                (userUid || 0) +
                '/'
            )
            const data = await res.json()
            const token = data.rtcToken
            uid.current = await client.join(appId, channel, token, userUid || 0)
          } catch (e) {
            console.log(e)
          }
        } else {
          uid.current = await client.join(
            appId,
            channel,
            token || null,
            userUid || 0
          )
        }
        // console.log('!uid: ', uid.current)
      } else {
        console.error('trying to join before RTC Engine was initialized')
      }
    }
    if (callActive) {
      join()
      console.log('Attempted join: ', rtcProps.channel)
    } else {
      console.log('In precall - waiting to join')
    }
    return (): void => {
      ignore = true
      if (callActive) {
        console.log('Leaving channel')
        try {
          stopScreenshare()
          isScreensharingRef.current = false
        } catch (e) {
          console.log(e)
        }
        canJoin.current = client
          .leave()
          .catch((err: unknown) => console.log(err))
      }
    }
  }, [rtcProps.channel, rtcProps.uid, callActive, rtcProps.tokenUrl])

  // publish local stream
  //
  // Re-runs whenever a track appears, is replaced (e.g. the camera started late or was retried)
  // or is re-enabled. Whether a track is already published is read from the client itself
  // (`client.localTracks`) rather than from a flag: the old `let ...HasPublished` flags were
  // reset on every render, and the effect never re-ran for a camera track that arrived after the
  // join, so a late camera was never sent to the other participant.
  useEffect(() => {
    if (!callActive || !channelJoined) return
    let cancelled = false

    const publishTrack = async (
      track: ILocalAudioTrack | ILocalVideoTrack | null | undefined,
      kind: 'audio' | 'video'
    ) => {
      if (cancelled || !track || !track.enabled) return
      if (client.localTracks.includes(track)) return
      try {
        console.log(`[RTCConfigure] Publishing ${kind} track`)
        await client.publish([track])
        console.log(`[RTCConfigure] ${kind} track published successfully`)
      } catch (error) {
        console.error(`[RTCConfigure] Failed to publish ${kind} track:`, error)
      }
    }

    const publish = async () => {
      if (rtcProps.enableDualStream && !dualStreamEnabledRef.current) {
        try {
          await client.enableDualStream()
          dualStreamEnabledRef.current = true
        } catch (error) {
          console.warn('[RTCConfigure] Could not enable dual stream:', error)
        }
      }
      await publishTrack(localAudioTrack, 'audio')
      await publishTrack(localVideoTrack, 'video')
    }

    publish()
    return () => {
      cancelled = true
    }
  }, [
    callActive,
    channelJoined,
    localAudioTrack,
    localVideoTrack,
    localVideoTrack?.enabled,
    localAudioTrack?.enabled
  ])

  // update local state if tracks are not null
  // Support audio-only mode: update state even if only audio track is available
  useEffect(() => {
    // Check if we have at least an audio track
    if (localAudioTrack !== null && localAudioTrack !== undefined) {
      // Update mediaStore with available tracks
      mediaStore.current[0] = {
        audioTrack: localAudioTrack,
        ...(localVideoTrack && { videoTrack: localVideoTrack })
      }

      // Dispatch update with available tracks
      dispatch({
        type: 'update-user-video',
        value: [localAudioTrack, localVideoTrack]
      })

      console.log('[RTCConfigure] Local tracks updated in mediaStore:', {
        hasAudio: !!localAudioTrack,
        hasVideo: !!localVideoTrack,
        isAudioOnly: !!localAudioTrack && !localVideoTrack
      })
    }
  }, [rtcProps.channel, channelJoined, localAudioTrack, localVideoTrack])

  // renew token if token is updated
  useEffect(() => {
    if (channelJoined && rtcProps.token) {
      client
        .renewToken(rtcProps.token)
        .then((e) => console.log('renewed token', e))
    }
  }, [rtcProps.token, channelJoined])

  // set role if role is updated
  useEffect(() => {
    if (rtcProps.role) {
      client
        .setClientRole(rtcProps.role)
        .then((e) => console.log('changed role', e))
    }
  }, [rtcProps.role, channelJoined])

  // active speaker
  useEffect(() => {
    async function enableActiveSpeaker() {
      if (rtcProps.activeSpeaker && rtcProps.layout !== layout.grid) {
        client.on('volume-indicator', (volumes) => {
          const highestvolumeObj = volumes.reduce(
            (
              highestVolume: {
                level: number
                uid: UID
              },
              volume
            ) => {
              if (highestVolume === null) {
                return volume
              } else {
                if (volume.level > highestVolume.level) {
                  return volume
                }
                return highestVolume
              }
            },
            null
          )
          const activeSpeaker = highestvolumeObj
            ? highestvolumeObj.uid
            : undefined
          const mapActiveSpeakerToZero =
            activeSpeaker === uid.current ? 0 : activeSpeaker
          if (activeSpeaker !== undefined) {
            dispatch({
              type: 'ActiveSpeaker',
              value: [mapActiveSpeakerToZero]
            })
          }
        })
        await client.enableAudioVolumeIndicator()
      }
    }
    if (callActive) {
      enableActiveSpeaker()
    }
    return () => {
      client.removeAllListeners('volume-indicator')
    }
  }, [rtcProps.activeSpeaker, rtcProps.layout])

  const toggleScreensharing = async () => {
    const start = async () => {
      dispatch({
        type: 'Screensharing',
        value: [true]
      })
      screenTrack.current = await AgoraRTC.createScreenVideoTrack({}, 'disable')
      const uid = rtcProps.screenshareUid || 1 // 1 is default
      mediaStore.current[uid] = { videoTrack: screenTrack.current }
      screenTrack.current.on('track-ended', () => {
        isScreensharingRef.current = false
        dispatch({
          type: 'Screensharing',
          value: [false]
        })
      })
      isScreensharingRef.current = true
      await startScreenshare(
        rtcProps.appId,
        rtcProps.channel,
        screenTrack.current,
        rtcProps.screenshareToken,
        rtcProps.screenshareUid,
        rtcProps.tokenUrl,
        rtcProps.enableDualStream
      )
    }

    const stop = () => {
      stopScreenshare()
      isScreensharingRef.current = false
    }

    if (isScreensharingRef.current) {
      stop()
    } else {
      start()
    }
  }

  return (
    <RtcProvider
      value={{
        client,
        mediaStore: mediaStore.current,
        localVideoTrack,
        localAudioTrack,
        dispatch,
        localUid: uid,
        channelJoined,
        toggleScreensharing: toggleScreensharing,
        isScreensharing: isScreensharingRef.current
      }}
    >
      <MaxUidProvider value={uidState.max}>
        <MinUidProvider value={uidState.min}>
          {/* <MinUidProvider value={uidState.min}> */}
          {ready ? props.children : null}
        </MinUidProvider>
      </MaxUidProvider>
    </RtcProvider>
  )
}

export default RtcConfigure
